import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Link from "next/link";
import SearchBlog from "./search-blog";
import "../../../styles/scss/blog-tags.module.scss";

export type TopicTagLink = { slug: string; name: string };

const TagHeroSection = ({
  tagName,
  tagDescription,
  topicTags = [],
  excludeSlug,
  searchInputPostDetails,
  showBackToTopics = true,
  showPopularTopics = true,
}: {
  tagName: string;
  tagDescription?: string;
  topicTags?: TopicTagLink[];
  /** Hide this topic slug in the list (page you are on). */
  excludeSlug?: string | null;
  /** When set (e.g. on `/topics`), shows blog search in the hero. */
  searchInputPostDetails?: unknown[];
  /** Show “Back to Topics” (hide on `/topics` index). */
  showBackToTopics?: boolean;
  /** `/topics` index: search only; hide the Popular Topics chip row in the hero. */
  showPopularTopics?: boolean;
}) => {
  const [isScrolled, setIsScrolled] = useState(false);

  const visibleTags = useMemo(() => {
    if (!excludeSlug) return topicTags;
    return topicTags.filter((t) => t.slug !== excludeSlug);
  }, [topicTags, excludeSlug]);

  const popularTopicsLabel = excludeSlug
    ? "Other Popular Topics"
    : "Popular Topics";

  /** Index passes `edges` (may be empty); slug pages omit this prop. */
  const showSearch = searchInputPostDetails != null;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 160 && isScrolled != true) {
        setIsScrolled(true);
      } else if (isScrolled != false) {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div className={`tag-hero-section ${isScrolled ? "active-scroll" : ""}`}>
        <div className={"container"}>
          <div className={"header-section"}>
            <div className={"d-flex"}>
              <div className={"left-block"}>
                {showBackToTopics ? (
                  <Link href={"/topics/"} className={"back-to-blog"}>
                    Back to Topics
                  </Link>
                ) : null}
                <h1>{tagName}</h1>
                <p>
                  {tagDescription ||
                    "Explore our step-by-step guides and best practices to expand your knowledge in Observability, OpenTelemetry and beyond."}
                </p>
              </div>
              <div className={"right-block"}>
                {showSearch ? (
                  <div className="tag-hero-search">
                    <SearchBlog searchInputPostDetails={searchInputPostDetails} />
                  </div>
                ) : null}
                {showPopularTopics ? (
                  <div className="tag-hero-popular-topics">
                    <div className="popular-tags">{popularTopicsLabel}</div>
                    <div className="tags">
                      {visibleTags.map(({ slug, name }) => (
                        <Link
                          key={slug}
                          href={`/topics/${slug}/`}
                          className="read-more"
                        >
                          {name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

TagHeroSection.propTypes = {
  tagName: PropTypes.string.isRequired,
  tagDescription: PropTypes.string,
  topicTags: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  excludeSlug: PropTypes.string,
  searchInputPostDetails: PropTypes.array,
  showBackToTopics: PropTypes.bool,
  showPopularTopics: PropTypes.bool,
};

export default TagHeroSection;
